import { useState } from "react";
import { useDashboard } from "../hooks/useMeters";
import {
  PageHeader,
  Spinner,
  ErrorState,
  StatCard,
} from "../components/common";
import { fmt } from "../utils/helpers";
import {
  Zap,
  Activity,
  Wifi,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import clsx from "clsx";
import Layout from "../../../layout";
import "../reportAnalytics.css";

export default function DashboardPage() {
  const { data, isLoading, error } = useDashboard();
  const [search, setSearch] = useState("");

  if (isLoading)
    return (
      <div className="p-6">
        <Spinner />
      </div>
    );
  if (error)
    return (
      <div className="p-6">
        <ErrorState message={String(error)} />
      </div>
    );

  const meters = data?.meters ?? [];
  const filtered = meters.filter((m) =>
    m.meter_id.toLowerCase().includes(search.toLowerCase()),
  );

  // Fleet summary stats
  const online = meters.filter((m) => m.relay_status).length;
  const totalKW = meters.reduce((s, m) => s + (m.kw ?? 0), 0);
  const avgVolt = meters.length
    ? meters.reduce((s, m) => s + (m.voltage ?? 0), 0) / meters.length
    : 0;
  const avgPF = meters.length
    ? meters.reduce((s, m) => s + (m.power_factor ?? 0), 0) / meters.length
    : 0;

  return (
    <Layout>
      <div className="p-0.5">
        <PageHeader
          title="Fleet Dashboard"
          subtitle="Real-time overview of all smart meters"
        >
          <input
            className="ra-select-sm w-48"
            placeholder="Search meter ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </PageHeader>

        {/* Fleet KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            label="Meters Online"
            value={`${online} / ${meters.length}`}
            color="emerald"
          />
          <StatCard
            label="Total Active KW"
            value={fmt.num(totalKW, 1)}
            unit="W"
            color="blue"
          />
          <StatCard
            label="Avg Voltage"
            value={fmt.num(avgVolt, 1)}
            unit="V"
            color="violet"
          />
          <StatCard
            label="Avg Power Factor"
            value={fmt.num(Math.abs(avgPF), 3)}
            color="amber"
          />
        </div>

        {/* Meter cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <MeterCard key={m.meter_id} meter={m} />
          ))}
        </div>
      </div>
    </Layout>
  );
}

function MeterCard({ meter: m }: { meter: any }) {
  const isOnline = m.relay_status;
  const util = m.load_utilisation_pct;

  return (
    <div
      className={clsx(
        "ra-card flex flex-col gap-3 hover:border-slate-600 transition-colors cursor-pointer",
        !isOnline && "opacity-60",
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <span className="font-mono text-sm font-semibold text-slate-900">
          {m.meter_id}
        </span>
        {isOnline ? (
          <span className="ra-badge-green">
            <CheckCircle size={11} />
            Online
          </span>
        ) : (
          <span className="ra-badge-red">
            <XCircle size={11} />
            Offline
          </span>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-2 text-xs">
        <Kpi
          icon={<Zap size={12} />}
          label="Active Power"
          value={`${fmt.num(m.kw, 1)} W`}
        />
        <Kpi
          icon={<Activity size={12} />}
          label="Voltage"
          value={`${fmt.num(m.voltage, 1)} V`}
        />
        <Kpi
          icon={<Wifi size={12} />}
          label="Frequency"
          value={`${fmt.num(m.frequency, 1)} Hz`}
        />
        <Kpi
          icon={<AlertTriangle size={12} />}
          label="Power Factor"
          value={fmt.num(m.power_factor, 3)}
        />
      </div>

      {/* Load utilisation bar */}
      {util != null && (
        <div>
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Load Utilisation</span>
            <span>{fmt.pct(util)}</span>
          </div>
          <div className="h-1.5 bg-slate-200 rounded-full overflow-hidden">
            <div
              className={clsx(
                "h-full rounded-full transition-all",
                util > 80
                  ? "bg-red-500"
                  : util > 50
                    ? "bg-amber-500"
                    : "bg-emerald-500",
              )}
              style={{ width: `${Math.min(util, 100)}%` }}
            />
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 mt-auto">{fmt.dt(m.timestamp)}</p>
    </div>
  );
}

function Kpi({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div className="bg-white border border-slate-300 rounded-lg px-2 py-1.5">
      <div className="flex items-center gap-1 text-slate-500 mb-0.5">
        {icon}
        <span className="text-xs">{label}</span>
      </div>
      <span className="font-mono font-semibold text-slate-900">{value}</span>
    </div>
  );
}
